import React, { useEffect } from "react";
import { useDescriptionStore } from "@/components/canvas/stores/DescriptionStore";
import { DescriptionSection } from "@/components/main/datasets/DescriptionSection";

interface TeamDescriptionProps {
  teamId: string;
  canEdit: boolean;
  /// Heading/body indent, forwarded to the section shell. Defaults (undefined)
  /// give the dataset-row subsection geometry so it aligns with a sibling
  /// section (e.g. Members in the Teams list). The single-team header, which
  /// has no chevron, overrides these to sit flush under its icon/name.
  headingPl?: string;
  bodyPl?: string;
  pr?: string;
}

/// Team description, backed by the shared description store (same source as the
/// old `EntityDescription entityType="team"`), rendered with the reusable
/// `DescriptionSection` so it matches the dataset row's Description subsection.
export const TeamDescription: React.FC<TeamDescriptionProps> = ({
  teamId,
  canEdit,
  headingPl,
  bodyPl,
  pr,
}) => {
  const {
    loadDescription,
    updateDescription,
    getDescription,
    isSaving,
    hasError,
  } = useDescriptionStore();
  const description = useDescriptionStore(() => getDescription(teamId));
  const saving = useDescriptionStore(() => isSaving(teamId));
  const error = useDescriptionStore(() => hasError(teamId));

  useEffect(() => {
    loadDescription("team", teamId);
  }, [teamId, loadDescription]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        loadDescription("team", teamId);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [teamId, loadDescription]);

  return (
    <DescriptionSection
      description={description || null}
      canEdit={canEdit}
      onSave={(next) => updateDescription("team", teamId, next ?? "")}
      saving={saving}
      error={error}
      headingPl={headingPl}
      bodyPl={bodyPl}
      pr={pr}
    />
  );
};
